/**
 * Utilitários de mapeamento e união entre reboques embutidos (legado)
 * e documentos vehicle com trailers: true.
 */

export function emptyReboque() {
  return {
    id: "",
    placaReboque: "",
    renavamReboque: "",
    ufReboque: "",
    descricaoReboque: "",
    rntrcReboque: "",
    tipoRNTRCReboque: "",
    TipoCarroceriaReboque: "",
    anttReboque: "",
    cnhReboque: "",
    capacidadeKGReboque: "",
    capacidadeM3Reboque: "",
    pesoTaraReboque: "",
    tipoProprietarioReboque: "cpf",
    cpfoucnpjReboque: "",
    nomeProprietarioReboque: "",
  };
}

export function normalizeTrailerIds(ids = []) {
  return Array.from(
    new Set(
      (ids || []).map((id) => String(id || "").trim()).filter(Boolean),
    ),
  );
}

/**
 * União de trailerIds do veículo (trator) com legado do motorista.
 * Deduplica preservando a ordem: primeiro veículo, depois motorista.
 */
export function mergeDriverVehicleTrailerIds(vehicleIds = [], driverLegacyIds = []) {
  return normalizeTrailerIds([
    ...(Array.isArray(vehicleIds) ? vehicleIds : []),
    ...(Array.isArray(driverLegacyIds) ? driverLegacyIds : []),
  ]);
}

export function unwrapVehicleResponse(response) {
  const body = response?.data;
  if (body?.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    return body.data;
  }
  if (body && typeof body === "object" && body.id) {
    return body;
  }
  return null;
}

/** Converte documento vehicle (trailers:true) para shape de formulário de reboque. */
export function trailerDocToReboqueForm(doc) {
  if (!doc || typeof doc !== "object") {
    return emptyReboque();
  }

  return {
    ...emptyReboque(),
    id: String(doc.id || "").trim(),
    placaReboque: doc.vehiclePlate || doc.placaReboque || "",
    renavamReboque: doc.renavam || doc.renavamReboque || "",
    ufReboque: doc.ufVeiculo || doc.ufReboque || "",
    descricaoReboque: doc.descricaoVeiculo || doc.descricaoReboque || "",
    rntrcReboque: doc.rntrc || doc.rntrcReboque || "",
    tipoRNTRCReboque: doc.tipoRNTRC || doc.tipoRNTRCReboque || "",
    TipoCarroceriaReboque: doc.bodyworkType || doc.TipoCarroceriaReboque || "",
    anttReboque: doc.documentAnttFrontImg || doc.anttReboque || "",
    cnhReboque: doc.cnhReboque || "",
    capacidadeKGReboque: doc.capacidadeKG || doc.capacidadeKGReboque || "",
    capacidadeM3Reboque: doc.capacidadeM3 || doc.capacidadeM3Reboque || "",
    pesoTaraReboque: doc.pesoTara || doc.pesoTaraReboque || "",
    tipoProprietarioReboque:
      doc.tipoProprietario || doc.tipoProprietarioReboque || "cpf",
    cpfoucnpjReboque: doc.cpfoucnpj || doc.cpfoucnpjReboque || "",
    nomeProprietarioReboque:
      doc.nomeProprietario || doc.nomeProprietarioReboque || "",
  };
}

export function buildTrailerDraftFromDoc(doc, { dirty = false } = {}) {
  return {
    source: "trailerId",
    data: trailerDocToReboqueForm(doc),
    dirty: Boolean(dirty),
    status: "ready",
  };
}

function mapReboqueBusinessFields(reboque) {
  return {
    vehiclePlate: reboque?.placaReboque || "",
    renavam: reboque?.renavamReboque || "",
    ufVeiculo: reboque?.ufReboque || "",
    descricaoVeiculo: reboque?.descricaoReboque || "",
    rntrc: reboque?.rntrcReboque || "",
    ...(reboque?.tipoRNTRCReboque
      ? { tipoRNTRC: reboque.tipoRNTRCReboque }
      : {}),
    bodyworkType: reboque?.TipoCarroceriaReboque || "",
    capacidadeKG: reboque?.capacidadeKGReboque || "",
    capacidadeM3: reboque?.capacidadeM3Reboque || "",
    pesoTara: reboque?.pesoTaraReboque || "",
    ...(reboque?.tipoProprietarioReboque
      ? { tipoProprietario: reboque.tipoProprietarioReboque }
      : {}),
    cpfoucnpj: reboque?.cpfoucnpjReboque || "",
    nomeProprietario: reboque?.nomeProprietarioReboque || "",
  };
}

/** Payload de create de documento reboque (trailers: true). */
export function reboqueFormToTrailerCreatePayload(reboque, uidShipper = "") {
  const antt = String(reboque?.anttReboque || "").trim();
  return {
    uidShipper: uidShipper || "",
    trailers: true,
    ...mapReboqueBusinessFields(reboque),
    ...(antt ? { documentAnttFrontImg: antt } : {}),
    trailerIds: [],
    reboques: [],
  };
}

/**
 * Payload de update de reboque: omite trailers/trailerIds/reboques.
 * ANTT só entra se houver URL não vazia (preserva referência existente na API).
 */
export function reboqueFormToTrailerUpdatePayload(reboque, uidShipper = "") {
  const antt = String(reboque?.anttReboque || "").trim();
  return {
    uidShipper: uidShipper || "",
    ...mapReboqueBusinessFields(reboque),
    ...(antt ? { documentAnttFrontImg: antt } : {}),
  };
}

/** @deprecated Preferir create/update específicos. Mantido para compatibilidade. */
export function reboqueFormToTrailerPayload(reboque, uidShipper = "") {
  return reboqueFormToTrailerCreatePayload(reboque, uidShipper);
}

export function embeddedReboqueToForm(reboque) {
  return {
    ...emptyReboque(),
    ...(reboque || {}),
    id: String(reboque?.id || "").trim(),
    tipoProprietarioReboque: reboque?.tipoProprietarioReboque || "cpf",
  };
}

export function mapEmbeddedReboqueToApiItem(reboque) {
  if (!reboque) return null;
  return {
    ...(reboque.id ? { id: reboque.id } : {}),
    placaReboque: reboque.placaReboque,
    renavamReboque: reboque.renavamReboque || "",
    ufReboque: reboque.ufReboque || "",
    descricaoReboque: reboque.descricaoReboque || "",
    rntrcReboque: reboque.rntrcReboque || "",
    ...(reboque.tipoRNTRCReboque
      ? { tipoRNTRCReboque: reboque.tipoRNTRCReboque }
      : {}),
    TipoCarroceriaReboque: reboque.TipoCarroceriaReboque || "",
    anttReboque: reboque.anttReboque || "",
    cnhReboque: reboque.cnhReboque || "",
    capacidadeKGReboque: reboque.capacidadeKGReboque || "",
    capacidadeM3Reboque: reboque.capacidadeM3Reboque || "",
    pesoTaraReboque: reboque.pesoTaraReboque || "",
    ...(reboque.tipoProprietarioReboque
      ? { tipoProprietarioReboque: reboque.tipoProprietarioReboque }
      : {}),
    cpfoucnpjReboque: reboque.cpfoucnpjReboque || "",
    nomeProprietarioReboque: reboque.nomeProprietarioReboque || "",
  };
}

/**
 * Mapeia array embutido para payload da API (filtra não iniciados se predicado informado).
 */
export function mapEmbeddedReboquesToApiPayload(reboques, isStarted = null) {
  const list = Array.isArray(reboques) ? reboques : [];
  const filtered =
    typeof isStarted === "function" ? list.filter(isStarted) : list;
  return filtered.map(mapEmbeddedReboqueToApiItem).filter(Boolean);
}

/**
 * União canônica: docs por trailerIds + reboques[] embutidos.
 * Não promove automaticamente; não deduz identidade pela placa.
 *
 * @returns {Array<{ source: 'trailerId'|'embedded', id: string, data: object }>}
 */
export function mergeVehicleTrailers(vehicle, trailerDocs = []) {
  const trailerIds = normalizeTrailerIds(vehicle?.trailerIds);
  const docsById = new Map(
    (trailerDocs || [])
      .filter((doc) => doc && doc.id)
      .map((doc) => [String(doc.id).trim(), doc]),
  );

  const fromIds = trailerIds.map((id) => {
    const doc = docsById.get(id);
    return {
      source: "trailerId",
      id,
      data: doc ? trailerDocToReboqueForm(doc) : { ...emptyReboque(), id },
    };
  });

  const embedded = Array.isArray(vehicle?.reboques) ? vehicle.reboques : [];
  const fromEmbedded = embedded
    .map((reboque, index) => {
      const id = String(reboque?.id || "").trim() || `embedded-${index}`;
      if (trailerIds.includes(id)) {
        return null;
      }
      return {
        source: "embedded",
        id,
        data: embeddedReboqueToForm({ ...reboque, id }),
      };
    })
    .filter(Boolean);

  return [...fromIds, ...fromEmbedded];
}

/** Rótulo de apresentação do seletor (placa + contexto). */
export function formatTrailerOptionLabel(reboqueOrDoc) {
  if (!reboqueOrDoc) return "Reboque";
  const plate =
    reboqueOrDoc.placaReboque ||
    reboqueOrDoc.vehiclePlate ||
    "";
  const body =
    reboqueOrDoc.TipoCarroceriaReboque ||
    reboqueOrDoc.bodyworkType ||
    "";
  const plateLabel = plate || "Sem placa";
  return body ? `${plateLabel} — ${body}` : plateLabel;
}

/**
 * Aplica atualização de um item embutido no array legado sem clobber dos demais.
 */
export function patchEmbeddedReboque(reboques, reboqueId, nextData) {
  const list = Array.isArray(reboques) ? [...reboques] : [];
  const index = list.findIndex(
    (item) => String(item?.id || "").trim() === String(reboqueId || "").trim(),
  );
  if (index < 0) {
    return list;
  }
  list[index] = {
    ...list[index],
    ...nextData,
    id: String(reboqueId || list[index].id || "").trim(),
  };
  return list;
}

export function createPendingStorageId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `pending-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
